/**
 * Client-side helpers for two-phase strategems: the local animation can finish before
 * `strategem-initiated` arrives (delayed play-turn + network). Without a flush, the client
 * never sends `strategem-animation-complete` and the server may fall back to a long timeout.
 */

/**
 * @param {object} p
 * @param {*} p.pendingAnimInfo - Entry from pendingStrategemAnimationIdsRef, if strategem-initiated already ran
 * @param {*} p.strategemInstanceId
 * @param {boolean} p.wsReady - WebSocket open and ready
 * @param {boolean} p.isSpectating
 * @param {boolean} p.isAutobattlerSpectator
 * @param {number} p.remainingAnimationsForStrategemCount - Other active animations for this cast (exclude current)
 * @returns {boolean} Whether to add strategemInstanceId to the deferred set
 */
export function shouldDeferStrategemAnimationComplete({
  pendingAnimInfo,
  strategemInstanceId,
  wsReady,
  isSpectating,
  isAutobattlerSpectator,
  remainingAnimationsForStrategemCount,
}) {
  if (pendingAnimInfo || strategemInstanceId == null || !wsReady || isSpectating || isAutobattlerSpectator) {
    return false;
  }
  return remainingAnimationsForStrategemCount === 0;
}

/**
 * When strategem-initiated arrives after the animation completed, send completion immediately.
 * Mutates `deferredSet` and `pendingMap` on success (same as BattleUI inline logic).
 *
 * @param {object} p
 * @param {Set<string>} p.deferredSet
 * @param {Map} p.pendingMap - strategemInstanceId -> { strategemAnimationId, ... }
 * @param {*} p.strategemInstanceId - Must match the key used when calling `pendingMap.set` from the server message
 * @param {boolean} p.wsReady
 * @param {boolean} p.isSpectating
 * @param {boolean} p.isAutobattlerSpectator
 * @returns {{ ok: false } | { ok: true, strategemAnimationId: string, strategemInstanceId: * }}
 */
export function tryFlushDeferredStrategemComplete({
  deferredSet,
  pendingMap,
  strategemInstanceId,
  wsReady,
  isSpectating,
  isAutobattlerSpectator,
}) {
  const deferredSid = strategemInstanceId != null ? String(strategemInstanceId) : null;
  if (
    !deferredSid ||
    !deferredSet.has(deferredSid) ||
    !wsReady ||
    isSpectating ||
    isAutobattlerSpectator ||
    strategemInstanceId == null
  ) {
    return { ok: false };
  }
  const pendingAnimInfo = pendingMap.get(strategemInstanceId);
  if (!pendingAnimInfo) {
    return { ok: false };
  }
  deferredSet.delete(deferredSid);
  pendingMap.delete(strategemInstanceId);
  return {
    ok: true,
    strategemAnimationId: pendingAnimInfo.strategemAnimationId,
    strategemInstanceId,
  };
}
